"use client";

import React, { useState, useEffect, useContext, useCallback } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import LineChartOne from "../../components/charts/line/LineChartOne";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "../../components/ui/table/index";
import Button from "../../components/ui/button/Button";
import useApi from "../../hooks/useApi";
import AuthContext from "../../context/AuthContext"; // Ajusta la ruta si es necesario
import CreateExcursionModal from "../../components/excursions/CreateExcursionModal";
import ReservaDetailsModal from "../../components/reservas/ReservaDetailsModal";
import TourReservationsListModal from "../../components/reservas/TourReservationsListModal";
import { useNavigate } from "react-router";
import { toast } from "react-hot-toast";
import RoleBasedRenderer from "../../components/auth/RoleBasedRenderer";

interface Metrics {
  totalIncome: string;
  totalIncomeChange: string;
  b2cProfit: string;
  b2cProfitChange: string;
  b2bProfit: string;
  b2bProfitChange: string;
  activeTours: string;
  confirmedBookings: string;
  pendingBookings: string;
  newReservationsPercentage: string;
}

interface UpcomingTour {
  name: string;
  date: string;
  bookings: number;
  totalPax: number;
  id?: number;
}

interface DashboardMetrics extends Omit<Metrics, 'totalBookings'> {
  activeBookings: number;    // Reservas activas (pending_payment, partial_payment, paid)
  inactiveBookings: number;  // Reservas inactivas (no_show, completed)
  totalBookings: number;     // Total de todas las reservas
}

export default function Dashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalIncome: "Cargando...",
    totalIncomeChange: "Cargando...",
    b2cProfit: "Cargando...",
    b2cProfitChange: "Cargando...",
    b2bProfit: "Cargando...",
    b2bProfitChange: "Cargando...",
    activeTours: "Cargando...",
    confirmedBookings: "Cargando...",
    pendingBookings: "Cargando...",
    newReservationsPercentage: "Cargando...",
    activeBookings: 0,
    inactiveBookings: 0,
    totalBookings: 0
  });
  const [recentBookings, setRecentBookings] = useState<any[]>([]);
  const [upcomingTours, setUpcomingTours] = useState<UpcomingTour[]>([]);
  const [chartData, setChartData] = useState<{ name: string; data: number[] }[]>([]);
  const [isNewExcursionModalOpen, setIsNewExcursionModalOpen] = useState(false);
  
  // State for reservation list modal
  const [selectedTour, setSelectedTour] = useState<UpcomingTour | null>(null);
  const [isReservationsListModalOpen, setIsReservationsListModalOpen] = useState(false);
  const [tourReservations, setTourReservations] = useState<Array<any>>([]);
  
  // State for individual reservation details modal
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [reservationType, setReservationType] = useState<"b2c" | "b2b">("b2c");

  // Pagination for recent bookings
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Number of items to show per page

  const { 
    fetchMetrics, 
    fetchRecentBookings, 
    fetchUpcomingTours, 
    processBookingTrends, 
    createExcursion, 
    fetchTourAllReservations,
    getReservas
  } = useApi();
  
  const { user, isLoading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  // Función auxiliar para determinar el color según el signo
  const getChangeColor = (value: string): "success" | "error" =>
    value.trim().startsWith("-") ? "error" : "success";

  // Función auxiliar para obtener clases CSS según el color
  const getTagClasses = (color: string): string => {
    switch (color) {
      case "success":
        return "bg-green-100 text-green-800";
      case "error":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "info":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Add this helper function at the top level of the component
  const getStatusDisplay = (status: string): { text: string; type: "success" | "warning" | "error" } => {
    switch (status) {
      case "pending_payment":
        return { text: "Pago pendiente", type: "warning" };
      case "partial_payment":
        return { text: "Pago parcial", type: "warning" };
      case "paid":
        return { text: "Pagada", type: "success" };
      case "no_show":
        return { text: "No show", type: "error" };
      case "completed":
        return { text: "Completada", type: "success" };
      default:
        return { text: status, type: "warning" };
    }
  };

  // Add this helper function at the top level of the component
  const formatCurrency = (value: string | number): string => {
    if (value === "Cargando...") return value;
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    // Formatear números grandes con sufijos k, M, etc.
    if (numValue >= 1000000) {
      return `$${(numValue / 1000000).toFixed(1)}M`;
    } else if (numValue >= 1000) {
      return `$${(numValue / 1000).toFixed(1)}k`;
    }
    
    // Formateo normal para números pequeños
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  };

  // Agregar esta función helper
  const getDaysRemaining = (dateStr: string): number => {
    const [day, month, year] = dateStr.split('/');
    const tourDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = tourDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Agregar esta función helper para el texto de días restantes
  const getDaysRemainingText = (days: number): string => {
    if (days === 0) return 'Hoy';
    if (days === 1) return 'Mañana';
    return `En ${days} días`;
  };

  // Updated function to handle tour click
  const handleTourClick = async (tour: UpcomingTour) => {
    try {
      setSelectedTour(tour);
      
      // Show loading state
      setTourReservations([]);
      setIsReservationsListModalOpen(true);
      
      // Fetch all reservations for this tour
      const reservations = await fetchTourAllReservations(tour.name, tour.date);
      
      if (reservations && reservations.length > 0) {
        setTourReservations(reservations);
      } else {
        toast.error("No se encontraron reservas para esta excursión");
      }
    } catch (error) {
      console.error("Error fetching tour reservations:", error);
      toast.error("Error al cargar las reservas de la excursión");
      setIsReservationsListModalOpen(false);
    }
  };

  // Function to handle individual reservation selection
  const handleReservationSelect = (reservation: any) => {
    setSelectedReservation(reservation);
    // Use the type property directly from the transformed reservation
    setReservationType(reservation.type === "b2c" ? "b2c" : "b2b");
    setIsReservationsListModalOpen(false);
    setIsDetailsModalOpen(true);
  };

  // Function to handle recent booking click
  const handleRecentBookingClick = async (booking: any) => {
    try {
      // Show loading state by opening the modal with basic data first
      const formattedReservation = {
        id: booking.id,
        clienteName: booking.client,
        excursion: booking.tour,
        fecha: booking.date,
        estado: booking.status,
        hotel: "Cargando...",
        adultos: 0,
        ninios: 0,
        metodoPago: "Cargando...",
        precioTotal: 0,
        montoFacturado: 0,
        saldoPendiente: 0,
        descuento: 0,
        notas: "",
        type: booking.type || "b2c"
      };
      
      setSelectedReservation(formattedReservation);
      setReservationType(booking.type === "b2b" ? "b2b" : "b2c");
      setIsDetailsModalOpen(true);
      
      // Fetch all reservations and find the one with matching ID
      const allReservations = await getReservas();
      
      if (allReservations && Array.isArray(allReservations)) {
        const completeReservation = allReservations.find(res => res.id === booking.id);
        
        if (completeReservation) {
          // Update the modal with complete data
          setSelectedReservation(completeReservation);
          setReservationType(completeReservation.type === "b2b" ? "b2b" : "b2c");
        } else {
          // If we can't find a reservation with that ID, keep the basic data
          toast.error("No se encontró la información completa de la reserva");
        }
      } else {
        toast.error("Error al cargar los datos de las reservas");
      }
    } catch (error) {
      console.error("Error displaying reservation details:", error);
      toast.error("Error al mostrar los detalles de la reserva");
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(recentBookings.length / itemsPerPage);
  const paginatedBookings = recentBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Pagination controls
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    // Only fetch data when user is authenticated and not during authentication loading
    if (authLoading || !user) return;
    
    let isMounted = true;
    
    async function fetchData() {
      try {
        console.log("Fetching dashboard data...");
        
        // Use Promise.all to fetch data in parallel
        const [metricsData, bookingsData, toursData] = await Promise.all([
          fetchMetrics(),
          fetchRecentBookings(),
          fetchUpcomingTours()
        ]);
        
        // Only update state if component is still mounted
        if (!isMounted) return;
        
        if (metricsData) setMetrics(metricsData);
        console.log("Bookings data received:", bookingsData); // Debug log
        setRecentBookings(bookingsData);
        setUpcomingTours(toursData);
      } catch (error) {
        toast.error("Error fetching dashboard data");
        // Add user-friendly error handling here if needed
      }
    }
    
    fetchData();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [
    // Reduce dependency array to only essential items
    authLoading, 
    user
    // Remove these function dependencies that may cause re-renders
    // fetchMetrics,
    // fetchRecentBookings, 
    // fetchUpcomingTours
  ]);

  useEffect(() => {
    if (recentBookings.length > 0) {
      setChartData(processBookingTrends(recentBookings));
    }
  }, [recentBookings]);

  if (authLoading || !user) {
    return <div>Cargando token...</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-[1920px] mx-auto">
      <PageMeta
        title="Panel de control | Destiny Caribbean Tours"
        description="Visión general del rendimiento operacional y financiero"
      />
      <PageBreadcrumb pageTitle="Panel principal" />

      {/* Layout principal */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Columna izquierda - Métricas financieras y gráfico */}
        <div className="xl:col-span-8 space-y-6">
          {/* Métricas financieras - 3 cards en fila */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Ingresos totales */}
            <RoleBasedRenderer requiredRoles={["admin"]} fallback={
              <ComponentCard className="w-full" title="Ingresos totales">
                <div className="w-full flex items-center justify-center py-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Acceso restringido
                  </span>
                </div>
              </ComponentCard>
            }>
              <ComponentCard className="w-full" title="Ingresos totales">
                <div className="w-full flex flex-col sm:flex-row flex-wrap justify-between items-start gap-2">
                  <div className="text-2xl font-semibold dark:text-white flex-1 truncate">
                    {formatCurrency(metrics.totalIncome)}
                  </div>
                  <span
                    className={`inline-block flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${getTagClasses(
                      getChangeColor(metrics.totalIncomeChange)
                    )}`}
                  >
                    {metrics.totalIncomeChange}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Últimos 30 días
                </p>
              </ComponentCard>
            </RoleBasedRenderer>

            {/* Rentabilidad B2C */}
            <RoleBasedRenderer requiredRoles={["admin"]} fallback={
              <ComponentCard className="w-full" title="Rentabilidad B2C">
                <div className="w-full flex items-center justify-center py-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Acceso restringido
                  </span>
                </div>
              </ComponentCard>
            }>
              <ComponentCard className="w-full" title="Rentabilidad B2C">
                <div className="w-full flex flex-col sm:flex-row flex-wrap justify-between items-start gap-2">
                  <div className="text-2xl font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(metrics.b2cProfit)}
                  </div>
                  <span
                    className={`inline-block flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${getTagClasses(
                      getChangeColor(metrics.b2cProfitChange)
                    )}`}
                  >
                    {metrics.b2cProfitChange}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Margen neto
                </p>
              </ComponentCard>
            </RoleBasedRenderer>

            {/* Rentabilidad B2B */}
            <RoleBasedRenderer requiredRoles={["admin"]} fallback={
              <ComponentCard className="w-full" title="Rentabilidad B2B">
                <div className="w-full flex items-center justify-center py-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Acceso restringido
                  </span>
                </div>
              </ComponentCard>
            }>
              <ComponentCard className="w-full" title="Rentabilidad B2B">
                <div className="w-full flex flex-col sm:flex-row flex-wrap justify-between items-start gap-2">
                  <div className="text-2xl font-semibold text-blue-600 dark:text-blue-400">
                    {formatCurrency(metrics.b2bProfit)}
                  </div>
                  <span
                    className={`inline-block flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${getTagClasses(
                      getChangeColor(metrics.b2bProfitChange)
                    )}`}
                  >
                    {metrics.b2bProfitChange}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Margen neto
                </p>
              </ComponentCard>
            </RoleBasedRenderer>
          </div>

          {/* Gráfico de tendencias */}
          <ComponentCard className="w-full" title="Tendencia de reservas">
            <div className="w-full h-[350px]">
              {chartData && chartData.length > 0 ? (
                <LineChartOne series={chartData} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  No hay datos disponibles
                </div>
              )}
            </div>
          </ComponentCard>

          {/* Reservas recientes */}
          <ComponentCard className="w-full" title="Reservas recientes">
            <div className="overflow-x-auto scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 dark:scrollbar-track-gray-700 dark:scrollbar-thumb-gray-500">
              <div className="min-w-[800px]">
                <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <TableHeader className="bg-gray-50 dark:bg-gray-800">
                    <TableRow>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6 w-[80px]">
                        ID
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6 min-w-[200px] max-w-[300px]">
                        Cliente
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6 min-w-[200px] max-w-[300px]">
                        Tour
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6 w-[120px]">
                        Fecha
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6 w-[130px]">
                        Estado
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {recentBookings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="px-4 py-4 text-sm text-center text-gray-500 dark:text-gray-400 sm:px-6">
                          No hay datos disponibles
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedBookings.map((booking) => (
                        <TableRow 
                          key={booking.id} 
                          className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                          onClick={() => handleRecentBookingClick(booking)}
                        >
                          <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6 whitespace-nowrap">
                            <div className="w-full h-full">
                              #{booking.id}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                            <div className="w-full h-full truncate" title={booking.client}>
                              {booking.client}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                            <div className="w-full h-full truncate" title={booking.tour}>
                              {booking.tour}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6 whitespace-nowrap">
                            <div className="w-full h-full">
                              {booking.date}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-4 text-sm sm:px-6 whitespace-nowrap">
                            <div className="w-full h-full">
                              {(() => {
                                const { text, type } = getStatusDisplay(booking.status);
                                return (
                                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getTagClasses(type)}`}>
                                    {text}
                                  </span>
                                );
                              })()}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination controls */}
              {recentBookings.length > itemsPerPage && (
                <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md 
                        ${currentPage === 1 
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                        border border-gray-300 dark:border-gray-600`}
                    >
                      Anterior
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                        ${currentPage === totalPages 
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                        border border-gray-300 dark:border-gray-600`}
                    >
                      Siguiente
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, recentBookings.length)}</span> a{" "}
                        <span className="font-medium">{Math.min(currentPage * itemsPerPage, recentBookings.length)}</span> de{" "}
                        <span className="font-medium">{recentBookings.length}</span> resultados
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                            ${currentPage === 1 
                              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                        >
                          <span className="sr-only">Anterior</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {/* Page numbers - limited to 5 pages */}
                        {(() => {
                          // Calculate which page numbers to show (max 5)
                          let startPage = Math.max(1, currentPage - 2);
                          let endPage = Math.min(totalPages, startPage + 4);
                          
                          // Adjust startPage if we're near the end
                          if (endPage - startPage < 4 && startPage > 1) {
                            startPage = Math.max(1, endPage - 4);
                          }
                          
                          const pages = [];
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                                  ${currentPage === i
                                    ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                              >
                                {i}
                              </button>
                            );
                          }
                          return pages;
                        })()}
                        
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                            ${currentPage === totalPages 
                              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                        >
                          <span className="sr-only">Siguiente</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ComponentCard>
        </div>

        {/* Columna derecha - Estados, próximas salidas y acciones */}
        <div className="xl:col-span-4 space-y-6">
          {/* Estado de Reservas */}
          <ComponentCard className="w-full" title="Estado de reservas">
            <div className="w-full flex flex-col space-y-4">
              {/* Total de reservas y porcentaje */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-lg sm:text-xl font-medium text-gray-900 dark:text-white">
                    {metrics.totalBookings} reservas
                  </span>
                  <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    Total de reservas
                  </span>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-medium ${
                  getTagClasses(getChangeColor(metrics.newReservationsPercentage))
                }`}>
                  {metrics.newReservationsPercentage}
                </span>
              </div>

              {/* Separador */}
              <div className="border-t border-gray-100 dark:border-gray-800"></div>

              {/* Grid de estados */}
              <div className="grid grid-cols-2 gap-3">
                {/* Reservas activas */}
                <div className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 shadow-sm">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                        {metrics.activeBookings}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        activas
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-100 dark:border-green-800/30">
                        {metrics.confirmedBookings} confirmadas
                      </span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-100 dark:border-yellow-800/30">
                        {metrics.pendingBookings} pendientes
                      </span>
                    </div>
                  </div>
                </div>

                {/* Reservas inactivas */}
                <div className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 shadow-sm">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                        {metrics.inactiveBookings}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        inactivas
                      </span>
                    </div>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 border border-gray-100 dark:border-gray-600/30">
                        Completadas/No-show
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Leyenda */}
              <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500">
                * Las reservas inactivas incluyen las completadas y no-show
              </p>
            </div>
          </ComponentCard>

          {/* Tours activos */}
          <ComponentCard className="w-full" title="Tours activos">
            <div className="w-full flex flex-col space-y-4">
              {/* Contador principal */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-lg sm:text-xl font-medium text-gray-900 dark:text-white">
                    {Number(metrics.activeTours) === 0 ? (
                      "Sin excursiones"
                    ) : (
                      `${metrics.activeTours} ${Number(metrics.activeTours) === 1 ? 'excursión' : 'excursiones'}`
                    )}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {Number(metrics.activeTours) === 0 
                      ? "No hay excursiones con reservas activas"
                      : "Con reservas activas"}
                  </span>
                </div>
              </div>

              {/* Separador */}
              <div className="border-t border-gray-100 dark:border-gray-800"></div>

              {/* Estadísticas detalladas */}
              <div className="grid grid-cols-2 gap-3">
                {/* Próximas salidas */}
                <div className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 shadow-sm">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                        {upcomingTours.length}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        próximas
                      </span>
                    </div>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/30">
                        Con salidas programadas
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ocupación promedio */}
                <div className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 shadow-sm">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                        {upcomingTours.reduce((acc, tour) => acc + tour.bookings, 0)}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        reservas
                      </span>
                    </div>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800/30">
                        Total de reservas activas
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Leyenda */}
              <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500">
                * Solo se muestran las excursiones con estado activo
              </p>
            </div>
          </ComponentCard>

          {/* Próximas salidas */}
          <ComponentCard className="w-full" title="Próximas salidas">
            <div className="space-y-3 max-h-[350px] overflow-y-auto scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 dark:scrollbar-track-gray-700 dark:scrollbar-thumb-gray-500">
              {upcomingTours.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
                  <svg 
                    className="w-20 h-20 mb-4 text-gray-200 dark:text-gray-700 animate-pulse" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                    />
                  </svg>
                  <p className="text-sm font-medium">No hay salidas programadas</p>
                </div>
              ) : (
                upcomingTours.map((tour: UpcomingTour, index) => {
                  const daysRemaining = getDaysRemaining(tour.date);
                  const isFullTour = tour.totalPax >= 20;
                  const isToday = daysRemaining === 0;
                  
                  return (
                    <div
                      key={index}
                      className={`relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 md:p-3 bg-white dark:bg-gray-800/50 rounded-xl border backdrop-blur-sm
                        ${isFullTour 
                          ? 'border-purple-200 dark:border-purple-500/30 shadow-lg shadow-purple-100/50 dark:shadow-purple-900/20' 
                          : isToday
                            ? 'border-indigo-200 dark:border-indigo-500/30 shadow-lg shadow-indigo-100/50 dark:shadow-indigo-900/20'
                            : 'border-gray-100 dark:border-gray-700'} 
                        group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors`}
                      onClick={() => handleTourClick(tour)}
                    >
                      {/* Indicador de tour lleno */}
                      {isFullTour && (
                        <div className="absolute top-2 right-2 sm:right-3">
                          <span className="flex h-2 w-2 sm:h-2.5 sm:w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 dark:bg-purple-500 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-full w-full bg-purple-500 dark:bg-purple-400"></span>
                          </span>
                        </div>
                      )}
                      
                      {/* Contenido principal */}
                      <div className="flex items-start space-x-3 md:space-x-4 w-full sm:w-auto overflow-hidden">
                        {/* Calendario */}
                        <div className="flex-shrink-0 flex flex-col items-center">
                          <div className={`w-12 h-12 sm:w-14 md:w-16 sm:h-14 md:h-16 flex items-center justify-center rounded-t-xl border-t border-x
                            ${isToday 
                              ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-500/30' 
                              : 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-600/30'}`}
                          >
                            <span className={`text-lg sm:text-xl md:text-2xl font-bold
                              ${isToday 
                                ? 'text-indigo-600 dark:text-indigo-400' 
                                : 'text-gray-700 dark:text-gray-300'}`}
                            >
                              {tour.date.split('/')[0]}
                            </span>
                          </div>
                          <div className={`w-12 sm:w-14 md:w-16 py-1 sm:py-1.5 md:py-2 text-center rounded-b-xl border border-t-0
                            ${isToday 
                              ? 'bg-indigo-100 dark:bg-indigo-900/50 border-indigo-200 dark:border-indigo-500/30' 
                              : 'bg-gray-100 dark:bg-gray-900/50 border-gray-200 dark:border-gray-600/30'}`}
                          >
                            <span className={`text-[10px] sm:text-xs font-medium
                              ${isToday 
                                ? 'text-indigo-700 dark:text-indigo-300' 
                                : 'text-gray-600 dark:text-gray-400'}`}
                            >
                              {getDaysRemainingText(daysRemaining)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Información del tour */}
                        <div className="flex-1 min-w-0 pr-2 sm:pr-0 overflow-hidden">
                          <h4 className="font-medium text-gray-900 dark:text-white truncate text-sm sm:text-base md:text-lg max-w-full">
                            {tour.name}
                          </h4>
                          <div className="flex items-center mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                            <svg className="flex-shrink-0 w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                              />
                            </svg>
                            <span className="truncate">{tour.date}</span>
                          </div>
                        </div>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 ml-0 sm:ml-0 mt-2 sm:mt-0 flex-shrink-0 w-full sm:w-auto">
                        <div className="flex items-center w-full sm:w-auto justify-start sm:justify-end">
                          <span className={`px-2 sm:px-2.5 md:px-3.5 py-1 md:py-1.5 rounded-full text-[10px] sm:text-xs font-medium ring-1 whitespace-nowrap
                            ${isFullTour 
                              ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 ring-purple-500/20 dark:ring-purple-400/20' 
                              : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-indigo-500/20 dark:ring-indigo-400/20'}`}
                          >
                            {tour.bookings} {tour.bookings === 1 ? 'reserva' : 'reservas'}
                          </span>
                        </div>
                        
                        <div className="flex items-center text-xs sm:text-sm">
                          <div className={`flex items-center px-2 sm:px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg
                            ${isFullTour 
                              ? 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30' 
                              : 'text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/30'}`}
                          >
                            <svg 
                              className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 md:mr-2 opacity-80" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                              />
                            </svg>
                            <span className="font-medium whitespace-nowrap">
                              {tour.totalPax} {tour.totalPax === 1 ? 'pasajero' : 'pasajeros'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ComponentCard>

          {/* Acciones rápidas */}
          <ComponentCard className="w-full" title="Acciones rápidas">
            <div className="space-y-3">
              <Button variant="primary" className="w-full" onClick={() => setIsNewExcursionModalOpen(true)}>
                Nueva excursión
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate('/clientes')}>
                Gestionar clientes
              </Button>
              <Button variant="outline" className="w-full">
                Ver facturación
              </Button>
            </div>
          </ComponentCard>
        </div>
      </div>

      {/* Modal de nueva excursión */}
      <CreateExcursionModal
        isOpen={isNewExcursionModalOpen}
        onClose={() => setIsNewExcursionModalOpen(false)}
        onAddExcursion={createExcursion}
        onSuccess={() => {
          fetchUpcomingTours();
        }}
      />

      {/* Reservations list modal */}
      <TourReservationsListModal
        isOpen={isReservationsListModalOpen}
        onClose={() => setIsReservationsListModalOpen(false)}
        tourName={selectedTour?.name || ""}
        tourDate={selectedTour?.date || ""}
        reservations={tourReservations}
        onSelectReservation={handleReservationSelect}
      />

      {/* Individual reservation details modal */}
      <ReservaDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedReservation(null);
        }}
        reserva={selectedReservation}
        type={reservationType}
      />
    </div>
  );
}

function getTagClasses(color: string): string {
  switch (color) {
    case "success":
      return "bg-green-100 text-green-800";
    case "error":
      return "bg-red-100 text-red-800";
    case "warning":
      return "bg-yellow-100 text-yellow-800";
    case "info":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}